import React from "react";
import { Box, Toolbar, useTheme } from "@mui/material";
import Topbar from "./Topbar";
import Sidebar from "./Sidebar";
import { CommandPalette } from "./CommandPalette";
import ThemeConfig from "../../ThemeProviderContext";
import {
  CommandPaletteProvider,
  useCommandPalette,
} from "../../hooks/useCommandPalette";
import { getAllNavigationItems } from "../../utils/navigationItems";
import lightDashboardBg from "../../assets/vehicle-icons/light-dashboard-bg.png";
import darkDashboardBg from "../../assets/vehicle-icons/dark-dashboard-bg.png";

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(true);
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleSidebarToggle = () => setSidebarCollapsed((prev) => !prev);

  return (
    <ThemeConfig>
      <CommandPaletteProvider>
        <ProviderContent
          onSidebarToggle={handleSidebarToggle}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          mobileOpen={mobileOpen}
          onMenuClick={handleDrawerToggle}
        >
          {children}
        </ProviderContent>
      </CommandPaletteProvider>
    </ThemeConfig>
  );
}

interface LayoutWrapperProps {
  children: React.ReactNode;
  sidebarCollapsed: boolean;
  onSidebarToggle: () => void;
  setSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  mobileOpen: boolean;
  onMenuClick: () => void;
}

function ProviderContent({
  children,
  sidebarCollapsed,
  onSidebarToggle,
  setSidebarCollapsed,
  mobileOpen,
  onMenuClick,
}: LayoutWrapperProps) {
  const { isOpen, close } = useCommandPalette();

  return (
    <>
      <CommandPalette
        open={isOpen}
        onClose={close}
        navigationItems={getAllNavigationItems()}
      />
      <LayoutWrapper
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        mobileOpen={mobileOpen}
        onSidebarToggle={onSidebarToggle}
        onMenuClick={onMenuClick}
      >
        {children}
      </LayoutWrapper>
    </>
  );
}

function LayoutWrapper({
  children,
  sidebarCollapsed,
  onSidebarToggle,
  setSidebarCollapsed,
  mobileOpen,
  onMenuClick,
}: LayoutWrapperProps) {
  const theme = useTheme();

  return (
    <Box
  sx={{
    display: "flex",
    width: "100%",
    minHeight: "100vh",
    position: "relative",
    overflow: "hidden",

    background:
      theme.palette.mode === "dark"
        ? "#030712"
        : "#EAF2FF",
  }}
>

<Box
  sx={{
    position: "fixed",
    inset: 0,
    zIndex: 0,

    backgroundImage: `url(${
      theme.palette.mode === "dark"
        ? darkDashboardBg
        : lightDashboardBg
    })`,

    backgroundSize: "cover",
    backgroundPosition: "center top",
    backgroundRepeat: "no-repeat",
    opacity: theme.palette.mode === "dark" ? 0.82 : 0.72,
    filter: theme.palette.mode === "dark" ? "saturate(1.08)" : "saturate(0.95)",
    transform: "scale(1.01)",

    transition:
      "background-image .4s ease, opacity .4s ease",

    pointerEvents: "none",
  }}
/>

<Box
  sx={{
    position: "fixed",
    inset: 0,
    zIndex: 1,

    background:
      theme.palette.mode === "dark"
        ? "linear-gradient(180deg, rgba(2,6,23,0.20) 0%, rgba(3,7,18,0.52) 58%, rgba(3,7,18,0.78) 100%)"
        : "linear-gradient(180deg, rgba(248,250,252,0.32) 0%, rgba(239,246,255,0.60) 60%, rgba(226,236,255,0.78) 100%)",

    pointerEvents: "none",
  }}
/>
<Box
  sx={{
    position: "relative",
    zIndex: 3,
    display: "flex",
    width: "100%",
  }}
>
  <Topbar
    onMenuClick={onMenuClick}
    onSidebarToggle={onSidebarToggle}
  />

  <Box onMouseLeave={() => setSidebarCollapsed(true)}>
    <Sidebar
      open={mobileOpen}
      onClose={onMenuClick}
      collapsed={sidebarCollapsed}
    />
  </Box>

  <Box
    component="main"
    sx={{
      flexGrow: 1,
      p: { xs: 1.25, sm: 1.5, md: 2 },
      width: {
        xs: "100%",
        sm: "100%",
        lg: sidebarCollapsed
          ? "calc(100% - 76px)"
          : "calc(100% - 244px)",
      },
      transition: "width .35s ease",
      minWidth: 0,
      overflowX: "hidden",
      mt: 1,
    }}
  >
    <Toolbar sx={{ minHeight: { xs: 50, sm: 56, md: 64 } }} />

    {children}
  </Box>
</Box>
    </Box>
  );
}
